import moment from 'moment';

/**
 * Builds `baseFinancialInstrumentInput` from a step-1 draft.
 *
 * Every instrument type nests the same base input, so the four create modals
 * were assembling the same ~30 keys by hand. Extracting it means a field that
 * moves on/off BaseFinancialInstrumentInput changes in one place — the class
 * of bug that produced the ticker/isin and countries rejections.
 *
 * Field set matches BaseFinancialInstrumentInput exactly; scripts/check-
 * instrument-inputs.mjs validates that against the live schema.
 */

// An empty fee schedule must be omitted, not sent as `false`.
const normaliseFeeSchedule = feeSchedule =>
  feeSchedule.map(fee => ({
    feeName: fee?.feeName,
    feeType: fee?.feeType,
    ...(fee?.fixedFeeAmount && {fixedFeeAmount: Number(fee.fixedFeeAmount)}),
    ...(fee?.feeType === 'ENTRY' && {
      level: fee?.level,
      // Orthogonal to level: charges entry once per exchange rather than once
      // overall. Sent as an explicit boolean so clearing it actually clears it.
      perExchange: Boolean(fee?.perExchange),
    }),
    ...(fee?.variableFeeAmount && {
      variableFeeAmount: Number(fee.variableFeeAmount),
    }),
    ...(fee?.scalable != undefined && {scalable: fee.scalable}),
    ...(fee?.feeType === 'RECURRING' && {
      frequency: fee?.frequency,
      origination: fee?.origination,
    }),
    ...(fee?.feeType === 'EXIT' &&
      Array.isArray(fee?.vector) && {
        vector: fee.vector.map(vect => ({
          duration: vect?.duration,
          magnitude: vect?.magnitude,
        })),
      }),
  }));

export const buildBaseInstrumentInput = ({
  key,
  assetKey,
  instrument = {},
  ticker,
  isin,
  currency,
}) => {
  const {
    name,
    description,
    icon,
    language,
    topics,
    countries,
    keyFacts,
    disclosures,
    documents,
    settings,
    groupId,
    sectorIndustry,
    exchange,
    invitedUsers,
    invitedGroups,
    listingType,
    marketPhase,
    riskLevel,
    tradeable,
    useInventoryForTrade,
    shariaCompliant,
    forDabaPro,
    enabledForFreeSimulation,
    fractionalShares,
    lotSize,
    allocatedUnits,
    minTradeableUnits,
    maxTradeableUnits,
    orderExpirationLimit,
    issuer,
    issuerType,
    offering,
    feeSchedule,
    startDate,
    endDate,
  } = instrument;

  return {
    key,
    assetClassKey: assetKey,
    name,
    description,
    icon,
    language,
    ticker,
    isin,
    topics,
    countries,
    keyFacts,
    disclosures,
    documents,
    settings,
    groupId,
    sectorIndustry,
    exchange,
    invitedUsers,
    invitedGroups,
    listingType,
    marketPhase,
    riskLevel,
    tradeable,
    useInventoryForTrade,
    shariaCompliant,
    forDabaPro,
    enabledForFreeSimulation,
    fractionalShares,
    lotSize,
    allocatedUnits,
    minTradeableUnits,
    maxTradeableUnits,
    orderExpirationLimit,
    ...(offering && {offering}),
    ...(startDate && {startDate: moment(startDate).format()}),
    ...(endDate && {endDate: moment(endDate).format()}),
    ...(issuer && {issuer: {id: issuer, issuerType}}),
    ...(currency && {
      currency: {
        name: currency.name,
        symbol: currency.symbol,
        currencyCode: currency.value,
      },
    }),
    ...(Array.isArray(feeSchedule) &&
      feeSchedule.length && {feeSchedule: normaliseFeeSchedule(feeSchedule)}),
  };
};

export default buildBaseInstrumentInput;
